import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.gen';
import { ToastProvider } from '@/components/ui';
import { AuthProvider } from '@/lib/auth-provider';
import { appIdentityScope, isDepartedPrivateKey } from '@/lib/identity-scope';
import './index.css';

/*
 * APP-W01c: the query cache is bound to the app identity scope. On every
 * identity transition the departing generation's private subtree is removed
 * from the cache — scoped key arrays are [userId, workspaceId, generation,
 * ...logical], so STRUCTURAL matching on the identity-prefix positions
 * removes exactly the departing generation's keys (active observers refetch
 * under their NEW scoped keys; the departing generation's fetches are
 * cancelled by removal).
 *
 * W01d-D2 correction: the purge predicate is structural key matching
 * (`isDepartedPrivateKey`) — key[0] vs the departed user, key[1] vs the
 * departed workspace, key[2] vs the departed generation. The previous
 * JSON-string-substring search could never match keys shaped like
 * [userId, workspaceId, generation, ...] for `gen:<n>` and silently purged
 * nothing. No string-substring matching remains.
 *
 * Refresh scoping preserved: run list stays distinct from run detail; the
 * QA and export scopes never invalidate each other; matching launch only
 * refreshes the scoped runs prefix (list + details) of the CURRENT identity.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status === 401 || status === 403) return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Transition invalidation: generation-first, then cancel/remove the
 * DEPARTING identity's scoped cache subtree. Runs on EVERY scope advance —
 * same-identifiers generation advance (permission change), A→B, and A→
 * unauthenticated (logout) all purge ONLY the departed private subtree.
 */
let lastGenerationBound = appIdentityScope.current().generation;
appIdentityScope.onInvalidate((prev, next) => {
  lastGenerationBound = next.generation;
  if (prev.generation === next.generation) return; // no transition, no purge
  for (const query of queryClient.getQueryCache().getAll()) {
    if (!isDepartedPrivateKey(query.queryKey, prev)) continue;
    void query.cancel().catch(() => { /* cancellation is best-effort here */ });
    queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
  }
});
void lastGenerationBound;

// Create router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>
);
